<script lang="ts">
  import InlineSvg from './InlineSvg.svelte';
  import Toggle from './Toggle.svelte';
  import { theme } from '../../store';
  import { LsKey, setStorage } from '../../lib/storage';

  let active = false;
  let menu: HTMLDivElement;
  let menuButton: HTMLDivElement;

  let contactLinks = [
    {
      text: 'Give Feedback',
      iconUrl: '/img/icon/feedback.svg',
      link: 'mailto:feedback@usepanda.com?subject=TheStocks Feedback',
    },
    {
      text: 'Suggest Source',
      iconUrl: '/img/icon/plus-circle.svg',
      link: 'mailto:feedback@usepanda.com?subject=TheStocks Suggest Source',
    },
    {
      text: 'Contact Us',
      iconUrl: '/img/icon/mail-open.svg',
      link: 'mailto:feedback@usepanda.com?subject=TheStocks Contact',
    },
    {
      text: 'Panda Network',
      iconUrl: '/img/icon/panda-network.svg',
      link: 'https://panda.network',
    },
  ];

  $: isDarkMode = $theme['dark-mode'];

  function toggleDarkMode() {
    isDarkMode = !isDarkMode;
    theme.set({ 'dark-mode': isDarkMode });
    setStorage(LsKey.DARK_MODE, isDarkMode);
  }

  function handleClick(event) {
    if (active && !menu.contains(event.target) && !menuButton.contains(event.target)) {
      active = false;
    }
  }
</script>

<svelte:window on:click={handleClick} />
<div class="menu" class:active bind:this={menu}>
  <ul>
    <li>
      <div>
        <InlineSvg src="/img/icon/dark-mode.svg" width="16px" />
        <span>Dark Mode</span>
        <Toggle size="m" checked={isDarkMode} on:click={toggleDarkMode} />
      </div>
    </li>
    {#each contactLinks as link (link.iconUrl)}
      <li>
        <a href={link.link} target="_blank">
          <InlineSvg src={link.iconUrl} width="16px" />
          <span>{link.text}</span>
        </a>
      </li>
    {/each}
  </ul>
</div>
<div class="menu-button" bind:this={menuButton} on:click={() => (active = !active)}>
  <div class="hamburger" class:active>
    <span />
  </div>
  <span>{active ? 'CLOSE' : 'MENU'}</span>
</div>

<style lang="scss">
  @import '../../styles/_mixins';

  .menu {
    opacity: 0;
    position: fixed;
    bottom: 36px;
    left: 10px;
    width: 188px;
    padding: 4px 0;
    color: var(--color-blue-gray--600);
    background-color: var(--color-blue-gray--50);
    border: 1px solid var(--color-blue-gray--100);
    border-radius: 10px;
    transition: all 0.1s ease-in;
    pointer-events: none;
    z-index: 3;

    a,
    div {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 12px 16px;

      span {
        @include font(Medium);
        font-weight: 500;
        font-size: 14px;
        line-height: 20px;
        margin-left: 12px;
        color: var(--color-blue-gray--600);
        user-select: none;
      }

      &:hover {
        span {
          color: var(--color-blue-gray--800);
        }

        :global(svg [stroke]:not([stroke='none'])) {
          stroke: var(--color-blue-gray--800) !important;
        }

        :global(svg [fill]:not([fill='none'])) {
          fill: var(--color-blue-gray--800) !important;
        }
      }
    }

    li:last-of-type::before {
      content: '';
      border-top: 1px solid var(--color-blue-gray--100);
      width: 100%;
      margin: 2px 0;
      display: block;
    }

    &.active {
      opacity: 1;
      bottom: 56px;
      pointer-events: all;

      & + .menu-button {
        color: var(--color-blue-gray--800);
        .hamburger span::before,
        .hamburger span::after {
          background: var(--color-blue-gray--800);
        }
      }
    }
  }

  .menu-button {
    position: fixed;
    bottom: 10px;
    left: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 188px;
    height: 36px;
    color: var(--color-blue-gray--300);
    background-color: var(--color-blue-gray--50);
    border: 1px solid var(--color-blue-gray--100);
    border-radius: 10px;
    transition: 0.1s ease-in-out;
    z-index: 3;

    cursor: pointer;
    &:active {
      transition: 0.1s ease-in-out;
      transform: scale(0.97);
    }

    .hamburger + span {
      @include font(Semibold);
      font-weight: 500;
      font-size: 13px;
      line-height: 20px;
    }

    .hamburger div {
      float: left;
      margin-right: 10%;
    }

    .hamburger div:last-child {
      margin-right: 0;
    }
    .hamburger {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 16px;
      height: 16px;
      cursor: pointer;
      margin-top: 1px;
    }

    &:hover {
      color: var(--color-blue-gray--800);
      background-color: var(--color-blue-gray--100);
      border-color: var(--color-blue-gray--150);
      .hamburger span,
      .hamburger span::before,
      .hamburger span::after {
        background: var(--color-blue-gray--800);
      }
    }

    .hamburger span,
    .hamburger span::before,
    .hamburger span::after {
      background: var(--color-blue-gray--300);
      border-radius: 3px;
      content: '';
      position: absolute;
      width: 8px;
      height: 1px;
      transition: 0.1s ease-in-out;
    }

    .hamburger span::before {
      margin-top: -3px;
    }

    .hamburger span::after {
      margin-top: 3px;
    }

    .hamburger.active span {
      background-color: transparent;
    }

    .hamburger.active span::before {
      margin-top: 0;
      transform: rotate(45deg) scale(1.35);
    }

    .hamburger.active span::after {
      margin-top: 0;
      transform: rotate(-45deg) scale(1.35);
    }

    span {
      user-select: none;
    }
  }

  :global(.dark-mode) {
    .menu {
      color: var(--color-blue-gray--300);
      background-color: var(--color-blue-gray--700);
      border: 1px solid var(--color-blue-gray--600);
      a,
      div {
        span {
          color: var(--color-blue-gray--200);
        }

        &:hover {
          span {
            color: white;
          }

          :global(svg [stroke]:not([stroke='none'])) {
            stroke: white !important;
          }

          :global(svg [fill]:not([fill='none'])) {
            fill: white !important;
          }
        }
      }

      li:last-of-type::before {
        border-top: 1px solid var(--color-blue-gray--600);
      }

      &.active {
        & + .menu-button {
          color: var(--color-blue-gray--150);
          .hamburger span::before,
          .hamburger span::after {
            background: var(--color-blue-gray--150);
          }
        }
      }
    }

    .menu-button {
      color: var(--color-blue-gray--300);
      background-color: var(--color-blue-gray--700);
      border: 1px solid var(--color-blue-gray--600);

      &:hover {
        color: var(--color-blue-gray--150);
        background-color: var(--color-blue-gray--600);
        border-color: var(--color-blue-gray--500);
        .hamburger span,
        .hamburger span::before,
        .hamburger span::after {
          background: var(--color-blue-gray--150);
        }
      }

      .hamburger span,
      .hamburger span::before,
      .hamburger span::after {
        background: var(--color-blue-gray--300);
      }
      .hamburger.active span {
        background-color: transparent;
      }
    }
  }

  @media screen and (max-width: 600px) {
    .menu {
      left: 50%;
      transform: translateX(-50%);
    }

    .menu-button {
      left: 50%;
      transform: translateX(-50%);
      width: 15%;
      min-width: 88px;
      &:active {
        left: 50%;
        transform: scale(0.97) translateX(-50%);
      }
    }
  }
</style>
